import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type ScheduleItem } from '@/lib/scheduleUtils';
import { GanttPrintError, getPrintableScheduleItems, openGanttPrintDialog } from '@/lib/ganttPrintExport';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
  items: ScheduleItem[];
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
}

export default function ScheduleExportPanel({ isOpen, onClose, projectName, items, dateRangeStart, dateRangeEnd }: Props) {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<string | null>(null);

  const runGanttPrint = useCallback(async (mode: 'print' | 'pdf') => {
    const snapshot = items.map((item) => ({ ...item }));
    const printable = getPrintableScheduleItems(snapshot);

    if (printable.length === 0) {
      setExportResult(t('exportPanel.noPrintableItems'));
      setTimeout(() => setExportResult(null), 5000);
      return;
    }

    setExporting(true);
    setExportResult(null);
    onClose();
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    try {
      await openGanttPrintDialog({
        projectName,
        items: snapshot,
        dateRangeStart,
        dateRangeEnd,
        showPdfHint: mode === 'pdf',
      });
      setExportResult(mode === 'pdf' ? t('exportPanel.pdfSuccess') : t('exportPanel.printSuccess'));
    } catch (err) {
      if (err instanceof GanttPrintError && err.code === 'POPUP_BLOCKED') {
        setExportResult(t('exportPanel.popupBlocked'));
      } else {
        setExportResult(mode === 'pdf' ? t('exportPanel.pdfFailed') : t('exportPanel.printFailed'));
      }
    } finally {
      setExporting(false);
      setTimeout(() => setExportResult(null), 5000);
    }
  }, [items, projectName, dateRangeStart, dateRangeEnd, onClose, t]);

  const handlePrintSchedule = useCallback(() => {
    void runGanttPrint('print');
  }, [runGanttPrint]);

  const handleExportPDF = useCallback(() => {
    void runGanttPrint('pdf');
  }, [runGanttPrint]);

  const handleExportCSV = useCallback(() => {
    setExporting(true);
    try {
      const headers = [
        'Task No',
        'Title',
        'Phase',
        'Stage',
        'Sub Stage',
        'Duration (days)',
        'Start Date',
        'End Date',
        'Progress %',
        'Complete',
        'Included',
        'Assigned To',
        'Assigned Role',
      ];
      const rows = items.map((item) => [
        item.task_no ?? '',
        item.title,
        item.phase || '',
        item.main_stage || '',
        item.construction_sub_stage || '',
        item.duration_days,
        item.start_date || '',
        item.end_date || '',
        item.progress,
        item.is_complete ? 'Yes' : 'No',
        item.is_included ? 'Yes' : 'No',
        item.assigned_to || '',
        item.assigned_role || '',
      ]);

      const csvContent =
        headers.join(',') +
        '\n' +
        rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectName.replace(/\s+/g, '_')}_schedule.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportResult(t('exportPanel.csvSuccess'));
    } catch {
      setExportResult(t('exportPanel.csvFailed'));
    } finally {
      setExporting(false);
      setTimeout(() => setExportResult(null), 3000);
    }
  }, [items, projectName, t]);

  if (!isOpen && !exportResult) return null;

  return (
    <>
      {exportResult && !isOpen && (
        <div
          className={`fixed bottom-4 right-4 z-[160] max-w-sm rounded-xl px-4 py-3 shadow-lg text-xs font-medium border ${
            exportResult.includes('failed') || exportResult.includes('失敗') || exportResult.includes('失败') || exportResult.includes('blocked') || exportResult.includes('ブロック') || exportResult.includes('阻止')
              ? 'bg-[#FEF2F2] text-[#EF4444] border-[#FECACA]'
              : 'bg-[#F0FDF4] text-[#059669] border-[#BBF7D0]'
          }`}
        >
          {exportResult}
        </div>
      )}

      {isOpen && (
    <div className="fixed inset-0 z-[150] flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden="true" />

      <div className="relative z-10 w-[380px] max-w-full bg-white shadow-2xl h-full flex flex-col">
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
              <i className="ri-download-line text-[#374151] text-sm"></i>
            </div>
            <h2 className="text-sm font-bold text-[#1A1A1A]">{t('exportPanel.title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-[#6B7280]"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-[#E8621A] hover:bg-[#FEF0E7]/30 transition-all cursor-pointer text-left disabled:opacity-50"
          >
            <div className="w-9 h-9 rounded-lg bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
              <i className="ri-file-pdf-line text-[#E8621A]"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#1A1A1A]">{t('exportPanel.exportToPDF')}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-0.5">{t('exportPanel.exportToPDFDesc')}</p>
            </div>
            <i className="ri-arrow-right-line text-[#9CA3AF] text-xs"></i>
          </button>

          <button
            onClick={handleExportCSV}
            disabled={exporting}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-[#E8621A] hover:bg-[#FEF0E7]/30 transition-all cursor-pointer text-left disabled:opacity-50"
          >
            <div className="w-9 h-9 rounded-lg bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
              <i className="ri-file-excel-line text-[#059669]"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#1A1A1A]">{t('exportPanel.exportToCSV')}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-0.5">{t('exportPanel.exportToCSVDesc')}</p>
            </div>
            <i className="ri-arrow-right-line text-[#9CA3AF] text-xs"></i>
          </button>

          <button
            onClick={handlePrintSchedule}
            disabled={exporting}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-[#E8621A] hover:bg-[#FEF0E7]/30 transition-all cursor-pointer text-left disabled:opacity-50"
          >
            <div className="w-9 h-9 rounded-lg bg-[#F9FAFB] flex items-center justify-center flex-shrink-0">
              <i className="ri-printer-line text-[#6B7280]"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#1A1A1A]">{t('exportPanel.print')}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-0.5">{t('exportPanel.printDesc')}</p>
            </div>
            <i className="ri-arrow-right-line text-[#9CA3AF] text-xs"></i>
          </button>

          <div className="rounded-xl bg-[#F9FAFB] p-3 mt-2">
            <div className="flex items-start gap-2">
              <i className="ri-information-line text-[#9CA3AF] text-xs mt-0.5"></i>
              <p className="text-[10px] text-[#9CA3AF] leading-relaxed">
                {t('exportPanel.note')}
              </p>
            </div>
          </div>

          {exportResult && (
            <div
              className={`rounded-xl p-3 text-center text-xs font-medium ${
                exportResult.includes('failed') || exportResult.includes('失敗') || exportResult.includes('失败') || exportResult.includes('blocked') || exportResult.includes('ブロック') || exportResult.includes('阻止')
                  ? 'bg-[#FEF2F2] text-[#EF4444]'
                  : 'bg-[#F0FDF4] text-[#059669]'
              }`}
            >
              {exportResult}
            </div>
          )}
        </div>
      </div>
    </div>
      )}
    </>
  );
}
